// src/modules/hr/pages/PerformanceManagement.jsx — Phase 21 (gold-business KPIs)
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Typography, Row, Col, Button, Select, Modal, Form, Input, InputNumber, message, Space, Popconfirm, Rate,
} from 'antd';
import { TrophyOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { usePermissions } from '../../../hooks/usePermissions';
import { fetchEmployees, fetchPerformance, upsertPerformance, deletePerformance } from '../api';

const { Title, Text } = Typography;
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

export default function PerformanceManagement() {
  const { can } = usePermissions();
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [periodKey, setPeriodKey] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchEmployees({ limit: 1000 }).then((r) => setEmployees(r?.data?.data || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (periodKey) params.periodKey = periodKey;
      const res = await fetchPerformance(params);
      setData(res?.data?.data || []);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [periodKey]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      await upsertPerformance(v);
      message.success('Saved'); setAddOpen(false); form.resetFields(); load();
    } catch (err) { if (!err?.errorFields) message.error(err?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const columns = [
    { title: 'Employee', dataIndex: ['employee', 'name'], render: (v, r) => r.employee?.name || '—' },
    { title: 'Period', dataIndex: 'periodKey', width: 100 },
    { title: 'Target', dataIndex: 'monthlyTarget', align: 'right', render: money },
    { title: 'Revenue', dataIndex: 'revenueGenerated', align: 'right', render: money },
    { title: 'Gold (g)', dataIndex: 'goldPurchasedGrams', align: 'right' },
    { title: 'Cases', dataIndex: 'casesClosed', align: 'right' },
    { title: 'Pledge Rev', dataIndex: 'pledgeRevenue', align: 'right', render: money },
    { title: 'Conv %', dataIndex: 'customerConversion', align: 'right', render: (v) => `${v || 0}%` },
    { title: 'Recovery %', dataIndex: 'recoveryPerformance', align: 'right', render: (v) => `${v || 0}%` },
    { title: 'Achieve %', dataIndex: 'achievementPercent', align: 'right', render: (v) => `${v || 0}%` },
    { title: 'Rating', dataIndex: 'rating', width: 120, render: (v) => <Rate disabled value={v || 0} style={{ fontSize: 12 }} /> },
    { title: '', key: 'a', width: 50, render: (_, r) => can('hrPerformance', 'delete') && <Popconfirm title="Delete review?" onConfirm={() => deletePerformance(r._id).then(() => { message.success('Deleted'); load(); })}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm> },
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}><TrophyOutlined style={{ marginRight: 8 }} />Performance Management</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Gold-business KPIs per employee/period</Text>
        </Col>
        <Col>
          <Space>
            <Input placeholder="Period e.g. 2026-06" style={{ width: 160 }} value={periodKey} onChange={(e) => setPeriodKey(e.target.value)} onPressEnter={load} allowClear />
            <Button icon={<ReloadOutlined />} onClick={load} />
            {can('hrPerformance', 'create') && <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add Review</Button>}
          </Space>
        </Col>
      </Row>

      <Table rowKey="_id" columns={columns} dataSource={data} loading={loading} size="small" scroll={{ x: 1300 }} pagination={{ pageSize: 50 }} />

      <Modal title="Performance Review" open={addOpen} onCancel={() => setAddOpen(false)} onOk={submit} okButtonProps={{ loading: saving }} width={640} destroyOnClose>
        <Form form={form} layout="vertical" initialValues={{ period: 'MONTHLY' }}>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="employee" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{employees.map((e) => <Select.Option key={e._id} value={e._id}>{e.name}</Select.Option>)}</Select></Form.Item></Col>
            <Col span={6}><Form.Item name="periodKey" label="Period Key" rules={[{ required: true }]}><Input placeholder="2026-06" /></Form.Item></Col>
            <Col span={6}><Form.Item name="period" label="Period"><Select options={[{ label: 'Monthly', value: 'MONTHLY' }, { label: 'Quarterly', value: 'QUARTERLY' }, { label: 'Annual', value: 'ANNUAL' }]} /></Form.Item></Col>
            <Col span={8}><Form.Item name="monthlyTarget" label="Target (₹)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="revenueGenerated" label="Revenue (₹)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="goldPurchasedGrams" label="Gold Purchased (g)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="casesClosed" label="Cases Closed"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="pledgeRevenue" label="Pledge Revenue (₹)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="customerConversion" label="Conversion %"><InputNumber style={{ width: '100%' }} min={0} max={100} /></Form.Item></Col>
            <Col span={8}><Form.Item name="recoveryPerformance" label="Recovery %"><InputNumber style={{ width: '100%' }} min={0} max={100} /></Form.Item></Col>
            <Col span={8}><Form.Item name="rating" label="Rating"><Rate /></Form.Item></Col>
          </Row>
          <Form.Item name="reviewerComments" label="Comments"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
